import React from 'react';

export const OrderCustomerStep = ({
  register,
  errors,
  handleSubmit,
  onCustomerSubmit,
  paymentMethod,
  setPaymentMethod,
  paymentReference,
  setPaymentReference,
  amountPaidUSD,
  setAmountPaidUSD,
  amountPaidLocal,
  setAmountPaidLocal,
  paymentStatus,
  setPaymentStatus,
  watchSameAsBilling,
  setStep,
  demoData
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7FBFC] to-[#E8F4F8] pt-14">
      {/* Sticky Header */}
      <div className="sticky top-14 z-10 bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <h2 className="text-xl font-bold text-[#011841]">Customer Details</h2>
          <p className="text-sm text-[#033EA5]">Enter customer information and payment details</p>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-3xl mx-auto px-4 py-4">
        <form onSubmit={handleSubmit(onCustomerSubmit)} className="space-y-6">
          {/* Demo Booking Info */}
          {demoData?.demoId && (
            <div className="bg-white rounded-xl shadow-lg border border-[#19BFB7]/20 p-4">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Demo ID:</span> {demoData.demoId}
              </p>
              <input type="hidden" {...register('demoBookingId')} />
            </div>
          )}

          {/* Mandatory Fields */}
          <div className="bg-[#F7FBFC] rounded-xl shadow-lg border border-[#19BFB7]/20 p-6">
            <h3 className="text-lg font-semibold mb-4 text-[#011841]">Required Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#022b73] mb-1">
                  Customer Name *
                </label>
                <input
                  type="text"
                  {...register('name', { required: 'Customer name is required' })}
                  className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841] placeholder-[#033EA5]/60"
                  placeholder="Enter customer full name"
                />
                {errors.name && (
                  <p className="text-[#033EA5] text-sm mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#022b73] mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  {...register('phone', { 
                    required: 'Phone number is required',
                    pattern: {
                      value: /^\+[1-9]\d{1,14}$/,
                      message: 'Please include country code (e.g., +97333123456)'
                    }
                  })}
                  className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841] placeholder-[#033EA5]/60"
                  placeholder="+97333123456 (Include country code)"
                />
                {errors.phone && (
                  <p className="text-[#033EA5] text-sm mt-1">{errors.phone.message}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">📱 Must include country code (e.g., +973, +971, +966, +63)</p>
              </div>

              {/* Payment Method Selection */}
              <div className="md:col-span-2 space-y-2">
                <label className="block text-sm font-medium text-[#022b73] mb-1">
                  Payment Method *
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841]"
                >
                  <option value="cash">Cash</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="payment_link">Payment Link</option>
                </select>
              </div>

              {/* Conditional Payment Details for Cash/Credit Card/Debit Card */}
              {(paymentMethod === 'cash' || paymentMethod === 'credit_card' || paymentMethod === 'debit_card') && (
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#022b73] mb-1">
                      Payment Reference *
                    </label>
                    {paymentMethod === 'cash' ? (
                      <select
                        value={paymentReference}
                        onChange={(e) => setPaymentReference(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841]"
                      >
                        <option value="CASH PAYMENT">CASH PAYMENT</option>
                        <option value="Paid to Bank">Paid to Bank</option>
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={paymentReference}
                        onChange={(e) => setPaymentReference(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841] placeholder-[#033EA5]/60"
                        placeholder="Enter transaction code or reference"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#022b73] mb-1">
                      Amount Paid USD *
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      value={amountPaidUSD}
                      onChange={(e) => setAmountPaidUSD(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841] placeholder-[#033EA5]/60"
                      placeholder="0.000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#022b73] mb-1">
                      Amount Paid Local Currency *
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      value={amountPaidLocal}
                      onChange={(e) => setAmountPaidLocal(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841] placeholder-[#033EA5]/60"
                      placeholder="0.000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#022b73] mb-1">
                      Payment Status *
                    </label>
                    <select
                      value={paymentStatus}
                      onChange={(e) => setPaymentStatus(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841]"
                    >
                      <option value="FULLY PAID">FULLY PAID</option>
                      <option value="PARTIAL PAID">PARTIAL PAID</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Conditional Payment Link Field */}
              {paymentMethod === 'payment_link' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#022b73] mb-1">
                    Payment Link (HTTPS) *
                  </label>
                  <input
                    type="url"
                    {...register('paymentLink', { 
                      required: paymentMethod === 'payment_link' ? 'Payment link is required' : false,
                      pattern: {
                        value: /^https:\/\//,
                        message: 'Payment link must be a secure HTTPS URL'
                      }
                    })}
                    className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841] placeholder-[#033EA5]/60"
                    placeholder="https://be.plsss.vip/pay"
                  />
                  {errors.paymentLink && (
                    <p className="text-[#033EA5] text-sm mt-1">{errors.paymentLink.message}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Optional Fields */}
          <div className="bg-[#F7FBFC] rounded-xl shadow-lg border border-[#19BFB7]/20 p-6">
            <h3 className="text-lg font-semibold mb-4 text-[#011841]">Optional Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#022b73] mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  {...register('email', {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Please enter a valid email address'
                    }
                  })}
                  className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841] placeholder-[#033EA5]/60"
                  placeholder="customer@example.com"
                />
                {errors.email && (
                  <p className="text-[#033EA5] text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-[#022b73] mb-3">Billing Address <span className="text-gray-400 text-xs">(optional)</span></h4>
                  <textarea
                    {...register('billingAddress')}
                    className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841] placeholder-[#033EA5]/60"
                    rows={3}
                    placeholder="Billing address (optional)"
                  />
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-[#022b73]">Shipping Address <span className="text-gray-400 text-xs">(optional)</span></h4>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="sameAsBilling"
                        {...register('sameAsBilling')}
                        className="h-4 w-4 text-[#19BFB7] focus:ring-[#19BFB7] rounded"
                      />
                      <label htmlFor="sameAsBilling" className="ml-2 text-xs text-[#022b73]">
                        Same as billing
                      </label>
                    </div>
                  </div>
                  <textarea
                    {...register('shippingAddress')}
                    className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841] placeholder-[#033EA5]/60"
                    rows={3}
                    placeholder="Shipping address (optional)"
                    disabled={watchSameAsBilling}
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#022b73] mb-1">
                  Notes
                </label>
                <textarea
                  {...register('notes')}
                  className="w-full px-4 py-3 border-2 border-[#19BFB7]/30 rounded-lg focus:border-[#19BFB7] focus:outline-none transition-colors bg-[#F7FBFC] text-[#011841] placeholder-[#033EA5]/60"
                  rows={3}
                  placeholder="Additional notes about the order (optional)"
                />
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={() => setStep('cart')}
              className="bg-[#F7FBFC] hover:bg-[#E8F4F8] text-[#022b73] px-6 py-3 rounded-lg font-medium transition-all duration-300 border-2 border-[#19BFB7]/30 hover:border-[#19BFB7] flex-1"
            >
              Back to Cart
            </button>
            <button
              type="submit"
              className="bg-gradient-to-r from-[#19BFB7] to-[#28E3DA] hover:from-[#17a8a0] hover:to-[#25d1c8] text-[#011841] px-6 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl flex-1"
            >
              Review Order
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
